/**
 * v2 Provisional Forecast — Edit screen.
 *
 * Route: /fdp/provisonal_forecast/edit?gfc=<gfc>&ndcs=<a,b,c>
 *
 * Layout (mirrors mock ProvisionalForecastUpdated/ProvisionalForecastEdit):
 *
 *   ROW 1 — Concept details panel (full width, top of page)
 *   ROW 2 — 80% main content (forecast tabs) | 20% Notes panel
 *
 * State split:
 *   - Concept details: pulled from the long-lived
 *     `ProvisionalForecastV2Store` so isConceptLoading flips
 *     synchronously when the user clicks "Edit" — that lets the
 *     skeleton render from frame zero and avoids a stale-concept flash
 *     on second visits.
 *   - Forecast batch + notes (multi-NDC): owned by a per-route store
 *     (`ProvisionalForecastV2EditStore`) instantiated by
 *     `<EditStoreProvider>`. Lives only for this Edit-route mount and
 *     resets on unmount, so re-entering the screen with a different
 *     NDC set never sees stale data.
 */

import { useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import { useSearchParams } from 'react-router-dom';
import { Skeleton } from 'primereact/skeleton';

import { useStore } from 'hooks/useStore';
import ConceptDetailsSection from 'screens/FDP/components/ConceptDetails';
import NotesSection from 'screens/FDPScreen/components/NotesSection';

import styles from './edit.module.scss';
import ForecastGridSkeleton from './ForecastGridSkeleton';
import ForecastTabs from './ForecastTabs';
import { EditStoreProvider, useEditStore } from './editStoreContext';

const ProvisionalForecastV2EditInner = observer(() => {
  const editStore = useEditStore();
  const [searchParams] = useSearchParams();
  const {
    ProvisionalForecastV2Store: {
      conceptDetails,
      contractDetails,
      isConceptLoading,
      resetAndFetchConcept,
    },
  } = useStore();

  // NDC-scoped notes live on the editStore now (per the v2 design —
  // each NDC has its own notes thread via FDP_Notes / NDC11Notes).
  // The legacy GFCNotes flow is no longer used here.
  const activeNDC = editStore.activeSlice?.ndc ?? null;
  const activeNDCNotes = activeNDC
    ? (editStore.notesByNDC.get(activeNDC) ?? [])
    : [];
  const isActiveNoteSaving = activeNDC
    ? !!editStore.isNoteSavingByNDC.get(activeNDC)
    : false;

  const { gfc, ndcs, parseError } = useMemo(() => {
    const gfcRaw = searchParams.get('gfc');
    const ndcsRaw = searchParams.get('ndcs');

    if (!gfcRaw) {
      return {
        gfc: null,
        ndcs: [] as string[],
        parseError:
          "Missing 'gfc' query param. Pick a concept from the search to begin.",
      };
    }
    const gfcNum = Number.parseInt(gfcRaw, 10);
    if (Number.isNaN(gfcNum)) {
      return {
        gfc: null,
        ndcs: [] as string[],
        parseError: `Invalid 'gfc' query param: ${gfcRaw}`,
      };
    }
    const ndcList = (ndcsRaw ?? '')
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
    if (ndcList.length === 0) {
      return {
        gfc: gfcNum,
        ndcs: [] as string[],
        parseError:
          "Missing 'ndcs' query param. Select at least one NDC from the sidebar to begin.",
      };
    }
    return { gfc: gfcNum, ndcs: ndcList, parseError: null };
  }, [searchParams]);

  // Pull concept details (and chained notes) on mount. Uses
  // resetAndFetchConcept (NOT handleGetConceptDetails) so the
  // previous concept's data is cleared synchronously and skeletons
  // render from frame zero rather than waiting for the API
  // round-trip to land. Re-runs only when gfc actually changes —
  // switching NDCs within the same GFC keeps the same concept
  // context, so we don't need to refetch concept details.
  useEffect(() => {
    if (gfc !== null) {
      resetAndFetchConcept(gfc);
    }
  }, [gfc, resetAndFetchConcept]);

  // Bulk-fetch the provisional forecasts AND notes in parallel for
  // every NDC in the URL. Both calls consume the same `?ndcs=` list,
  // so coupling them sequentially (notes-after-forecast) would only
  // add wall-clock latency. The store handles de-dupe + ordering for
  // each call internally.
  //
  // Re-keys on the joined NDC string so adding/removing/reordering
  // NDCs in the URL triggers a fresh load of both.
  const ndcsKey = useMemo(() => ndcs.join(','), [ndcs]);
  useEffect(() => {
    if (ndcs.length === 0) return;
    void editStore.loadForNDCs(ndcs);
    void editStore.prefetchNotes(ndcs);
    // ndcsKey identifies the request; ndcs reference also covered
    // since useMemo above keeps its identity stable per joined value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ndcsKey]);

  const handleAddNote = (note: string) => {
    if (!activeNDC) return;
    void editStore.addNoteFor(activeNDC, note);
  };

  if (parseError) {
    return (
      <div className={styles.editContainer}>
        <div className={styles.errorBox}>{parseError}</div>
      </div>
    );
  }

  return (
    <div className={styles.editContainer}>
      {/* ROW 1 — Concept details (full width).
          ConceptDetailsSection itself doesn't render a loading state,
          so we gate it on isConceptLoading and show a lightweight
          PrimeReact Skeleton placeholder during the fetch. This
          prevents both the empty-shell flash on first visit AND the
          "previous concept's data still showing" flicker on second
          visit — resetAndFetchConcept clears the observable state
          synchronously, so isConceptLoading is true while
          conceptDetails is null. */}
      {isConceptLoading || !conceptDetails ? (
        <div className={styles.conceptContainer}>
          <div className={styles.conceptSkeleton}>
            <Skeleton width="50%" height="1.6rem" className={styles.skelLine} />
            <Skeleton width="100%" height="3rem" className={styles.skelLine} />
            <Skeleton width="75%" height="1rem" className={styles.skelLine} />
          </div>
        </div>
      ) : (
        <div className={styles.conceptContainer}>
          <ConceptDetailsSection
            conceptDetails={conceptDetails}
            contractDetails={contractDetails}
            mode="edit"
            path="/fdp/provisonal_forecast"
            backTo="/fdp/provisonal_forecast"
          />
        </div>
      )}

      {/* ROW 2 — 80% main content (forecast tabs) | 20% Notes */}
      <div className={styles.mainAndNotesRow}>
        <div className={styles.mainColumn}>
          {editStore.isLoading ? (
            <ForecastGridSkeleton />
          ) : editStore.loadError ? (
            <div className={styles.errorBox}>{editStore.loadError}</div>
          ) : (
            <ForecastTabs />
          )}
        </div>

        <div className={styles.notesColumn}>
          <NotesSection
            mode="edit"
            isNotesDataLoading={editStore.isNotesLoading}
            isNoteAddLoading={isActiveNoteSaving}
            notesData={activeNDCNotes}
            handleAddNote={handleAddNote}
            isAddNewButtonDisabled={!activeNDC}
          />
        </div>
      </div>
    </div>
  );
});

const ProvisionalForecastV2Edit = () => (
  <EditStoreProvider>
    <ProvisionalForecastV2EditInner />
  </EditStoreProvider>
);

export default ProvisionalForecastV2Edit;
